import { encounterField } from '../../shared/array-types.js';
import { ecId, ecLean, itemField, record } from '../ability/context.js';

type Row = Record<string, unknown>;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** Legendary interludes and lair main turns each permit one top-level action. */
export function legendaryWindowKey(encounter: Row | null, actorId?: string): string | null {
  if (Number(encounterField(encounter, 'in_combat') ?? 0) !== 1) return null;
  const current = upper(encounterField(encounter, 'current_encounter_creature_id_turn'));
  if (actorId !== undefined && current !== upper(actorId)) return null;
  const phase = upper(encounterField(encounter, 'turn_phase') ?? 'main');
  const rows = encounter?.encounter_creatures_arr;
  const holder = (rows !== null && typeof rows === 'object' ? Object.values(rows) : []).map(record).find(row => ecId(row) === current);
  if (phase !== 'LEGENDARY' && (phase !== 'MAIN' || !holder || upper(ecLean(holder, 'item_type') ?? itemField(holder, 'item_type')) !== 'LAIR')) return null;
  return JSON.stringify([phase, ...['encounter_id', 'active_combat_id', 'current_round', 'current_encounter_creature_id_turn',
    'pending_main_turn_encounter_creature_id', 'legendary_turn_queue'].map(key => encounterField(encounter, key) ?? '')]);
}

export interface LegendaryWindowActions {
  claim(encounterId: string, actorId: string, operationId: string, definitionAvailable?: boolean, abilityType?: string): string | null;
  validate(operationId: string): string | null;
  bind(reservationId: string, operationId: string): void;
  accepted(operationId: string, revision: number): void;
  rejected(operationId: string): void;
  reset(encounterId: string): void;
}

/** One controller in the websocket bundle; other bundles use the window bridge. */
export function createLegendaryWindowActions(read: {
  encounter(id: string): Row | null;
  pending(encounterId: string, actorId: string): number;
  advance(input: { encounterId: string; actorId: string; legendaryWindowKey: string; retry?: boolean }): Promise<'handled' | 'refused' | 'failed'>;
  refuse(reason: string): void;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}): LegendaryWindowActions {
  type Claim = { encounterId: string; actorId: string; key: string; settling: boolean; cancelled?: boolean; consumedKey?: string };
  const windows = new Map<string, Claim>(), operations = new Map<string, Claim>();
  let storage = read.storage;
  try { storage ??= typeof window !== 'undefined' ? window.localStorage : undefined; } catch { /* Storage may be disabled. */ }
  const storageKey = (id: string): string => `ft.legendary-window.consumed:${upper(id)}`;
  const consumed = (id: string): string | null => { try { return storage?.getItem(storageKey(id)) ?? null; } catch { return null; } };
  // A completed turn then rewound to the same slot has a newer holder revision.
  // Capture it AFTER the action saves; the action itself also increments it.
  const consumedKey = (encounter: Row | null, key: string | null, revision?: number): string | null => {
    if (!key) return null;
    const current = upper(encounterField(encounter, 'current_encounter_creature_id_turn'));
    const rows = encounter?.encounter_creatures_arr;
    const holder = (rows !== null && typeof rows === 'object' ? Object.values(rows) : []).map(record).find(row => ecId(row) === current);
    return JSON.stringify([key, revision ?? Number(ecLean(holder ?? {}, 'movement_rev') ?? 0)]);
  };
  const persist = (id: string, key: string | null): void => {
    try { if (key === null) storage?.removeItem(storageKey(id)); else storage?.setItem(storageKey(id), key); } catch { /* The in-page reservation remains. */ }
  };
  const forget = (claim: Claim): void => {
    for (const [id, held] of operations) if (held === claim) operations.delete(id);
  };
  const release = (claim: Claim): void => {
    claim.cancelled = true;
    if (windows.get(claim.key) !== claim) return;
    windows.delete(claim.key);
    if (consumed(claim.encounterId) === claim.consumedKey) persist(claim.encounterId, null);
  };
  return {
    claim(encounterId, actorId, operationId, definitionAvailable = true, abilityType) {
      const encounter = read.encounter(encounterId);
      const key = legendaryWindowKey(encounter, actorId);
      const currentKey = legendaryWindowKey(encounter);
      const currentConsumedKey = consumedKey(encounter, currentKey);
      for (const claim of windows.values()) if (upper(claim.encounterId) === upper(encounterId) && claim.key !== currentKey) release(claim);
      if (consumed(encounterId) !== currentConsumedKey) persist(encounterId, null);
      if (!key) return null;
      // The legacy lair hook ran only after a lair ability; GM tools and reactions
      // on that row do not consume its main turn.
      if (upper(encounterField(encounter, 'turn_phase')) !== 'LEGENDARY' && abilityType !== 'lair_ability') return null;
      if (!definitionAvailable) return 'legendary-window:action-definition-unavailable';
      if (windows.has(key) || consumed(encounterId) === currentConsumedKey) return 'legendary-window:one-top-level-action';
      if (read.pending(encounterId, actorId)) return 'legendary-window:finish-the-move-first';
      const claim = { encounterId, actorId, key, settling: false };
      windows.set(key, claim); operations.set(operationId, claim);
      return null;
    },
    validate(operationId) {
      const claim = operations.get(operationId);
      return claim && (claim.cancelled || legendaryWindowKey(read.encounter(claim.encounterId)) !== claim.key) ? 'legendary-window:changed-before-action' : null;
    },
    bind(reservationId, operationId) {
      const claim = operations.get(reservationId);
      if (claim) operations.set(operationId, claim);
    },
    accepted(operationId, revision) {
      const claim = operations.get(operationId);
      if (!claim || claim.settling) return;
      if (claim.cancelled) { forget(claim); return; }
      claim.settling = true;
      // A lethal action may already have advanced the window in its own commit.
      if (legendaryWindowKey(read.encounter(claim.encounterId)) !== claim.key) { release(claim); forget(claim); return; }
      claim.consumedKey = consumedKey(read.encounter(claim.encounterId), claim.key, revision)!;
      persist(claim.encounterId, claim.consumedKey);
      void (async () => {
        for (const retry of [false, true]) {
          if (claim.cancelled) return;
          if (legendaryWindowKey(read.encounter(claim.encounterId)) !== claim.key) { release(claim); return; }
          try {
            const result = await read.advance({ encounterId: claim.encounterId, actorId: claim.actorId, legendaryWindowKey: claim.key, retry });
            if (result === 'handled') { release(claim); return; }
            // Network delivery already retried the same immutable operation.
            if (result === 'failed') break;
          } catch { /* Retry a failed plan once; never release the consumed action. */ }
        }
        if (!claim.cancelled && legendaryWindowKey(read.encounter(claim.encounterId)) === claim.key)
          read.refuse('legendary-window:advance-unavailable — End Turn to continue');
      })().finally(() => forget(claim));
      // Retain the consumed identity if delivery fails/rolls back. End Turn can
      // repair the boundary; another top-level action cannot spend again.
    },
    rejected(operationId) {
      const claim = operations.get(operationId);
      if (!claim || claim.settling) return;
      release(claim); forget(claim);
    },
    reset(encounterId) {
      for (const claim of windows.values()) if (upper(claim.encounterId) === upper(encounterId)) release(claim);
      persist(encounterId, null);
    },
  };
}

export function pageLegendaryWindowActions(): LegendaryWindowActions | undefined {
  return (window as unknown as { ftLegendaryWindowActions?: LegendaryWindowActions }).ftLegendaryWindowActions;
}
